import React, { useState } from 'react'
import Datetime from 'react-datetime'
import 'react-datetime/css/react-datetime.css'
import { Calendar as CalendarIcon, Clock, Zap, TrendingUp } from 'lucide-react'

interface ModernSchedulePostProps {
  pickerValue: any
  setPickerValue: React.Dispatch<any>
  switchValue: boolean
  setSwitchValue: React.Dispatch<React.SetStateAction<boolean>>
}

const recommendedTimes = [
  {
    time: '9:00 AM',
    day: 'Weekdays',
    reason: 'Morning commute',
    engagement: 'High',
    hour: 9,
  },
  {
    time: '12:00 PM',
    day: 'Daily',
    reason: 'Lunch break',
    engagement: 'Very High',
    hour: 12,
  },
  {
    time: '6:00 PM',
    day: 'Weekdays',
    reason: 'After work',
    engagement: 'High',
    hour: 18,
  },
  {
    time: '8:00 PM',
    day: 'Daily',
    reason: 'Evening relaxation',
    engagement: 'Very High',
    hour: 20,
  },
]

export default function ModernSchedulePost({
  pickerValue,
  setPickerValue,
  switchValue,
  setSwitchValue,
}: ModernSchedulePostProps): JSX.Element {
  const [showRecommendations, setShowRecommendations] = useState(true)

  const applyRecommendedTime = (hour: number) => {
    const now = new Date()
    now.setHours(hour, 0, 0, 0)
    // If the time is earlier than now, schedule for tomorrow
    if (now < new Date()) {
      // Time is today and in the future
    } else {
      now.setDate(now.getDate() + 1)
    }
    setPickerValue(now)
    setSwitchValue(true)
  }

  return (
    <div>
      <h2
        style={{
          fontSize: '32px',
          fontWeight: 700,
          background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: '12px',
        }}
      >
        Schedule Your Post
      </h2>
      <p
        style={{
          fontSize: '16px',
          color: 'rgba(255, 255, 255, 0.6)',
          marginBottom: '32px',
        }}
      >
        Choose when you want your content to go live
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
        {/* Left Column - Scheduling Options */}
        <div>
          {/* Post Now / Schedule Toggle */}
          <div
            style={{
              padding: '24px',
              borderRadius: '16px',
              background: 'rgba(30, 41, 59, 0.4)',
              border: '1px solid rgba(168, 85, 247, 0.2)',
              marginBottom: '24px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <CalendarIcon size={24} style={{ color: '#a855f7' }} />
                <h3
                  style={{
                    fontSize: '20px',
                    fontWeight: 600,
                    color: 'rgba(255, 255, 255, 0.9)',
                    margin: 0,
                  }}
                >
                  Schedule Post
                </h3>
              </div>

              {/* Toggle Switch */}
              <div
                onClick={() => setSwitchValue(!switchValue)}
                style={{
                  width: '56px',
                  height: '32px',
                  borderRadius: '16px',
                  background: switchValue
                    ? 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)'
                    : 'rgba(255, 255, 255, 0.1)',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  boxShadow: switchValue
                    ? '0 0 15px rgba(168, 85, 247, 0.5)'
                    : 'none',
                }}
              >
                <div
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: '#fff',
                    position: 'absolute',
                    top: '4px',
                    left: switchValue ? '28px' : '4px',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                  }}
                />
              </div>
            </div>

            <p
              style={{
                fontSize: '14px',
                color: 'rgba(255, 255, 255, 0.6)',
                margin: 0,
              }}
            >
              {switchValue
                ? 'Your post will be published at the scheduled time'
                : 'Your post will be published immediately'}
            </p>
          </div>

          {/* Date Time Picker */}
          {switchValue && (
            <div
              style={{
                padding: '24px',
                borderRadius: '16px',
                background: 'rgba(30, 41, 59, 0.4)',
                border: '1px solid rgba(168, 85, 247, 0.2)',
              }}
              className="modern-datetime-picker"
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '16px',
                }}
              >
                <Clock size={20} style={{ color: '#a855f7' }} />
                <h4
                  style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: 'rgba(255, 255, 255, 0.9)',
                    margin: 0,
                  }}
                >
                  Select Date & Time
                </h4>
              </div>

              <Datetime
                value={pickerValue}
                open={true}
                onChange={(value) => {
                  setPickerValue(value)
                }}
                inputProps={{
                  placeholder: 'Select date and time',
                  readOnly: true,
                  style: {
                    fontSize: '16px',
                    padding: '12px 16px',
                    backgroundColor: 'rgba(15, 23, 42, 0.6)',
                    color: '#fff',
                    border: '1px solid rgba(168, 85, 247, 0.3)',
                    borderRadius: '12px',
                    width: '100%',
                    outline: 'none',
                    cursor: 'pointer',
                  },
                }}
              />

              {pickerValue && (
                <div
                  style={{
                    marginTop: '16px',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    background: 'rgba(168, 85, 247, 0.1)',
                    border: '1px solid rgba(168, 85, 247, 0.2)',
                  }}
                >
                  <p
                    style={{
                      fontSize: '14px',
                      color: 'rgba(255, 255, 255, 0.7)',
                      margin: 0,
                    }}
                  >
                    📅 Scheduled for:{' '}
                    <strong style={{ color: '#fff' }}>
                      {new Date(pickerValue._d || pickerValue).toLocaleString()}
                    </strong>
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Column - Recommendations */}
        <div>
          <div
            style={{
              padding: '24px',
              borderRadius: '16px',
              background: 'rgba(30, 41, 59, 0.4)',
              border: '1px solid rgba(168, 85, 247, 0.2)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '20px',
              }}
            >
              <Zap size={24} style={{ color: '#fbbf24' }} />
              <h3
                style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  color: 'rgba(255, 255, 255, 0.9)',
                  margin: 0,
                }}
              >
                Best Times to Post
              </h3>
            </div>

            <p
              style={{
                fontSize: '14px',
                color: 'rgba(255, 255, 255, 0.6)',
                marginBottom: '20px',
              }}
            >
              Based on typical engagement patterns, these are the best times to reach your
              audience:
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {recommendedTimes.map((timeSlot, index) => (
                <div
                  key={index}
                  onClick={() => applyRecommendedTime(timeSlot.hour)}
                  style={{
                    padding: '16px',
                    borderRadius: '12px',
                    background: 'rgba(15, 23, 42, 0.6)',
                    border: '1px solid rgba(168, 85, 247, 0.15)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(168, 85, 247, 0.1)'
                    e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.4)'
                    e.currentTarget.style.transform = 'translateX(4px)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(15, 23, 42, 0.6)'
                    e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.15)'
                    e.currentTarget.style.transform = 'translateX(0)'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: '18px',
                          fontWeight: 600,
                          color: 'rgba(255, 255, 255, 0.9)',
                          marginBottom: '4px',
                        }}
                      >
                        {timeSlot.time}
                      </div>
                      <div
                        style={{
                          fontSize: '13px',
                          color: 'rgba(255, 255, 255, 0.5)',
                          marginBottom: '8px',
                        }}
                      >
                        {timeSlot.day}
                      </div>
                      <div
                        style={{
                          fontSize: '13px',
                          color: 'rgba(255, 255, 255, 0.6)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        <TrendingUp size={14} style={{ color: '#a855f7' }} />
                        {timeSlot.reason}
                      </div>
                    </div>
                    <div
                      style={{
                        padding: '4px 12px',
                        borderRadius: '8px',
                        background:
                          timeSlot.engagement === 'Very High'
                            ? 'rgba(34, 197, 94, 0.15)'
                            : 'rgba(168, 85, 247, 0.15)',
                        border: `1px solid ${
                          timeSlot.engagement === 'Very High'
                            ? 'rgba(34, 197, 94, 0.3)'
                            : 'rgba(168, 85, 247, 0.3)'
                        }`,
                        fontSize: '12px',
                        fontWeight: 600,
                        color:
                          timeSlot.engagement === 'Very High' ? '#22c55e' : '#a855f7',
                      }}
                    >
                      {timeSlot.engagement}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div
              style={{
                marginTop: '20px',
                padding: '14px',
                borderRadius: '12px',
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
              }}
            >
              <p
                style={{
                  fontSize: '13px',
                  color: 'rgba(255, 255, 255, 0.7)',
                  margin: 0,
                  lineHeight: '1.5',
                }}
              >
                💡 <strong>Tip:</strong> Click on any time slot to automatically schedule
                your post for that time today (or tomorrow if the time has passed).
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modern DateTime Picker Styles */}
      <style>
        {`
          .modern-datetime-picker .rdtPicker {
            background: rgba(15, 23, 42, 0.95);
            color: #e0e0e0;
            border: 1px solid rgba(168, 85, 247, 0.3);
            border-radius: 16px;
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
            padding: 16px;
            margin-top: 8px;
          }

          .modern-datetime-picker td.rdtDay:hover,
          .modern-datetime-picker td.rdtMonth:hover,
          .modern-datetime-picker td.rdtYear:hover {
            background: rgba(168, 85, 247, 0.2);
            border-radius: 8px;
          }

          .modern-datetime-picker .rdtTimeToggle:hover,
          .modern-datetime-picker button.rdtBtn:hover {
            background: rgba(168, 85, 247, 0.2);
          }

          .modern-datetime-picker td.rdtActive,
          .modern-datetime-picker td.rdtActive:hover {
            background: linear-gradient(135deg, #a855f7 0%, #ec4899 100%) !important;
            color: #fff !important;
            border-radius: 8px;
          }

          .modern-datetime-picker td.rdtDay,
          .modern-datetime-picker td.rdtMonth,
          .modern-datetime-picker td.rdtYear {
            color: #e0e0e0;
            padding: 8px;
            border-radius: 8px;
            transition: all 0.2s ease;
          }

          .modern-datetime-picker td.rdtOld,
          .modern-datetime-picker td.rdtNew {
            color: #666;
          }

          .modern-datetime-picker td.rdtDisabled,
          .modern-datetime-picker td.rdtDisabled:hover {
            color: #444;
            cursor: not-allowed;
            background: transparent;
          }

          .modern-datetime-picker .rdtPrev span,
          .modern-datetime-picker .rdtNext span,
          .modern-datetime-picker th.rdtSwitch,
          .modern-datetime-picker .rdtCounter,
          .modern-datetime-picker .rdtTimeToggle,
          .modern-datetime-picker .rdtCounterSeparator {
            color: #e0e0e0;
          }

          .modern-datetime-picker thead tr:first-of-type th:hover {
            background: rgba(168, 85, 247, 0.2);
            border-radius: 8px;
          }

          .modern-datetime-picker thead tr:first-child th,
          .modern-datetime-picker thead tr:last-child th {
            color: #e0e0e0;
            padding: 8px;
          }

          .modern-datetime-picker tfoot td,
          .modern-datetime-picker .rdtCount,
          .modern-datetime-picker button.rdtBtn {
            color: #e0e0e0;
          }

          .modern-datetime-picker .rdtCounters {
            background: rgba(30, 41, 59, 0.5);
            border-radius: 12px;
            padding: 12px;
          }

          .modern-datetime-picker .rdtCounter .rdtBtn {
            height: 32px;
            line-height: 32px;
            border-radius: 8px;
          }
        `}
      </style>
    </div>
  )
}